import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

export function DiscountBox({ imageIds, currency, applied, onApplied }) {
  const [code, setCode] = useState(applied?.code || "");
  const [busy, setBusy] = useState(false);
  const idsKey = imageIds.join(",");

  const validate = async (c, silent = false) => {
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: c, image_ids: imageIds });
      onApplied(data);
      if (!silent) toast.success(`Code ${data.code} applied — saving ${formatPrice(data.discount_total, currency)}`);
    } catch (e) {
      onApplied(null);
      if (!silent) toast.error(formatApiError(e));
      else toast.info(`Code ${c} no longer applies to your basket`);
    } finally {
      setBusy(false);
    }
  };

  // Re-check the applied code whenever the basket contents change.
  useEffect(() => {
    if (applied?.code) validate(applied.code, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  if (applied) {
    return (
      <div className="border border-[var(--pm-orange-40)] bg-[var(--pm-orange)]/5 p-4" data-testid="discount-applied">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold" data-testid="discount-applied-code">{applied.code}</p>
            <p className="text-sm text-white/80 font-condensed mt-1">
              {applied.description}
              {applied.photographer_name && <span className="text-white/50"> · {applied.photographer_name}</span>}
            </p>
            {applied.eligible_count < applied.basket_count && (
              <p className="text-xs text-white/50 font-condensed mt-1" data-testid="discount-partial">
                Applies to {applied.eligible_count} of {applied.basket_count} photos in your basket
              </p>
            )}
          </div>
          <button
            onClick={() => { onApplied(null); setCode(""); }}
            className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-red-400 shrink-0"
            data-testid="discount-remove"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-2 font-semibold">Discount code</label>
      <div className="flex gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") validate(code); }}
          placeholder="e.g. TRACKDAY10"
          className="input-minimal flex-1 uppercase"
          data-testid="discount-input"
        />
        <button
          onClick={() => validate(code)}
          disabled={!code || busy}
          className="border border-white/25 px-4 text-xs uppercase tracking-[0.2em] font-semibold hover:border-[var(--pm-orange)] disabled:opacity-40 shrink-0"
          data-testid="discount-apply"
        >
          {busy ? "…" : "Apply"}
        </button>
      </div>
    </div>
  );
}
